import { BorderTable } from '@/components/BorderTable';
import { ErrorBoundary } from '@/components/Rollbar';
import { FetchImportExportHistoriesArgs } from '@/context/services/import_export_histories/ImportExportHistories.service';
import { useImportExportHistoriesColumns } from '@/features/import_export_histories/Table/Columns/column';
import { sorterToSortBy } from '@/features/import_export_histories/Table/helpers/toSortBy';
import { useImportExportHistoriesTable } from '@/features/import_export_histories/Table/hooks/useImportExportHistoriesTable';
import {
  IMPORT_EXPORT_HISTORIES_PER_PAGE,
  MAX_POLLING_DURATION_MS,
  POLL_INTERVAL_MS,
} from '@/features/import_export_histories/constants';
import {
  ImportExportHistoriesColumn,
  OnCancelClick,
  OnDetailsClick,
  OnDownloadClick,
} from '@/features/import_export_histories/type';
import { useWarningTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import {
  ModernPaginationProps,
  ModernPagingProps,
  TableProps,
} from '@moneyforward/ap-frontend-components';
import {
  FilterValue,
  SorterResult,
  TablePaginationConfig,
} from 'antd/es/table/interface';
import {
  FC,
  Suspense,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';

export type ImportExportHistoriesTableProps = Omit<
  TableProps<ImportExportHistoriesColumn>,
  'columns' | 'pagination' | 'onChange'
>;

type Props = {
  table: ImportExportHistoriesTableProps;
  onDownloadClick?: OnDownloadClick;
  onCancelClick?: OnCancelClick;
  onDetailsClick?: OnDetailsClick;
  pagination: ModernPagingProps;
  onTableChange?: (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<ImportExportHistoriesColumn>
      | SorterResult<ImportExportHistoriesColumn>[]
  ) => void;
  sortBy?: string;
};

export const ImportExportHistoriesTable: FC<Props> = ({
  table,
  onDownloadClick,
  onCancelClick,
  onDetailsClick,
  pagination,
  onTableChange,
  sortBy,
}) => {
  const columns = useImportExportHistoriesColumns({
    onDownloadClick,
    onCancelClick,
    onDetailsClick,
    isLoading: table.loading,
    sortBy,
  });

  return (
    <BorderTable
      {...table}
      legacy={false}
      columns={columns}
      pagination={pagination}
      hideHeaderPagination
      rowKey='id'
      testId='import-export-history-table'
      onChange={onTableChange}
      scroll={TABLE_SCROLL}
    />
  );
};
ImportExportHistoriesTable.displayName = 'ImportExportHistoriesTable';

type ImportExportHistoriesTableViewProps = {
  searchQuery: FetchImportExportHistoriesArgs;
  onUpdateSearchQuery: (
    updates: Partial<FetchImportExportHistoriesArgs>
  ) => void;
  onCancelClick: OnCancelClick;
  onDetailsClick: OnDetailsClick;
  onDownloadClick: OnDownloadClick;
  isPageLoading?: boolean;
  isPollingEnabled: boolean;
};

type InnerTableProps = {
  searchQuery: FetchImportExportHistoriesArgs;
  onUpdateSearchQuery: (
    updates: Partial<FetchImportExportHistoriesArgs>
  ) => void;
  onCancelClick: OnCancelClick;
  onDetailsClick: OnDetailsClick;
  onDownloadClick: OnDownloadClick;
  isPageLoading?: boolean;
  isPollingEnabled: boolean;
};

const InnerTable: FC<InnerTableProps> = memo(
  ({
    searchQuery,
    onUpdateSearchQuery,
    onCancelClick,
    onDetailsClick,
    onDownloadClick,
    isPageLoading,
    isPollingEnabled,
  }) => {
    const {
      pagination: _pagination,
      table,
      data,
    } = useImportExportHistoriesTable({
      searchQuery,
      isPageLoading,
      refetchInterval: isPollingEnabled ? POLL_INTERVAL_MS : false,
    });

    const { send: sendWarning } = useWarningTrackingSystem();

    // When polling ends (isPollingEnabled: true → false), warn if a job is still
    // processing and has been running for at least MAX_POLLING_DURATION_MS.
    const wasPollingRef = useRef(false);
    useEffect(() => {
      if (wasPollingRef.current && !isPollingEnabled) {
        const hasProcessing = data.some((item) => {
          if (item.status !== 'processing') return false;
          const executedAtMs = new Date(item.executed_at).getTime();
          if (Number.isNaN(executedAtMs)) return false;
          return Date.now() - executedAtMs >= MAX_POLLING_DURATION_MS;
        });
        if (hasProcessing) {
          sendWarning('Import/export polling gave up after 5 minutes');
        }
      }
      wasPollingRef.current = isPollingEnabled;
    }, [isPollingEnabled, data, sendWarning]);

    const handleTableChange = useCallback(
      (
        _pagination: TablePaginationConfig,
        _filters: Record<string, FilterValue | null>,
        sorter:
          | SorterResult<ImportExportHistoriesColumn>
          | SorterResult<ImportExportHistoriesColumn>[]
      ) => {
        const sort_by = sorterToSortBy(sorter);
        onUpdateSearchQuery({ sort_by });
      },
      [onUpdateSearchQuery]
    );

    const onPageChange = useCallback(
      (page: number) => {
        onUpdateSearchQuery({ page });
      },
      [onUpdateSearchQuery]
    );

    const pagination: ModernPaginationProps = useMemo(
      () => ({
        ..._pagination,
        onChange: onPageChange,
        currentPage: searchQuery.page ?? 1,
        showPerPage: true,
        currentPerPage: IMPORT_EXPORT_HISTORIES_PER_PAGE,
      }),
      [_pagination, onPageChange, searchQuery.page]
    );

    return (
      <ImportExportHistoriesTable
        table={table}
        pagination={pagination}
        onDownloadClick={onDownloadClick}
        onCancelClick={onCancelClick}
        onDetailsClick={onDetailsClick}
        onTableChange={handleTableChange}
        sortBy={searchQuery.sort_by}
      />
    );
  }
);
InnerTable.displayName = 'InnerTable';

const TABLE_SCROLL = { x: 'max-content' } as const;

const ERROR_TABLE_PROPS = {
  data: [],
  loading: false,
  emptyType: 'error' as const,
};
const EMPTY_PAGINATION = {
  currentPage: 1,
  maxPage: 1,
  perPage: 1,
  maxRecord: 0,
};
const LOADING_TABLE_PROPS = { data: [], loading: true };

export const ImportExportHistoriesTableError: FC = memo(() => {
  return (
    <ImportExportHistoriesTable
      table={ERROR_TABLE_PROPS}
      pagination={EMPTY_PAGINATION}
    />
  );
});
ImportExportHistoriesTableError.displayName = 'ImportExportHistoriesTableError';

export const ImportExportHistoriesTableLoading: FC = memo(() => {
  return (
    <ImportExportHistoriesTable
      table={LOADING_TABLE_PROPS}
      pagination={EMPTY_PAGINATION}
    />
  );
});
ImportExportHistoriesTableLoading.displayName =
  'ImportExportHistoriesTableLoading';

export const ImportExportHistoriesTableView: FC<ImportExportHistoriesTableViewProps> =
  memo((props) => {
    const {
      searchQuery,
      onUpdateSearchQuery,
      onCancelClick,
      onDownloadClick,
      onDetailsClick,
      isPageLoading,
      isPollingEnabled,
    } = props;

    return (
      <ErrorBoundary fallback={ImportExportHistoriesTableError}>
        <Suspense fallback={<ImportExportHistoriesTableLoading />}>
          <InnerTable
            searchQuery={searchQuery}
            onUpdateSearchQuery={onUpdateSearchQuery}
            isPageLoading={isPageLoading}
            onCancelClick={onCancelClick}
            onDetailsClick={onDetailsClick}
            onDownloadClick={onDownloadClick}
            isPollingEnabled={isPollingEnabled}
          />
        </Suspense>
      </ErrorBoundary>
    );
  });
ImportExportHistoriesTableView.displayName = 'ImportExportHistoriesTableView';
